import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

interface UseGuidedTourOptions {
  userId?: string | null;
  apiBase: string;
  flag: string;
  enabled?: boolean;
  autoStart?: boolean;
  ready?: boolean;
}

type TourFlags = Record<string, boolean>;

export function getTourElement(id: string, fallbackId?: string): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  return (
    (document.querySelector(`[data-tour-id="${id}"]`) as HTMLElement | null) ||
    (fallbackId ? (document.querySelector(`[data-tour-id="${fallbackId}"]`) as HTMLElement | null) : null)
  );
}

export function tourTarget(id: string, fallbackId?: string): () => HTMLElement {
  return () => {
    const target = getTourElement(id, fallbackId);
    if (target) return target;
    if (typeof document !== 'undefined' && document.body) return document.body;
    return {} as HTMLElement;
  };
}

export function useGuidedTour({
  userId,
  apiBase,
  flag,
  enabled = true,
  autoStart = false,
  ready = true,
}: UseGuidedTourOptions) {
  const storageKey = useMemo(() => (userId ? `tourFlags:${userId}` : null), [userId]);
  const [flags, setFlags] = useState<TourFlags>({});
  const [initialized, setInitialized] = useState(false);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(0);
  const autoStartedRef = useRef(false);
  const seen = Boolean(flags[flag]);

  useEffect(() => {
    if (!storageKey) {
      setFlags({});
      setInitialized(true);
      return;
    }
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          setFlags(parsed);
        }
      }
    } catch {
      // Best-effort cache only.
    }
    if (process.env.NODE_ENV === 'test') {
      setInitialized(true);
      return;
    }
    let cancelled = false;
    const params = new URLSearchParams({ userId: userId || '' });
    getAuthHeaders()
      .then(headers => fetch(`${apiBase}/user/ui-flags?${params.toString()}`, { headers }))
      .then(response => (response.ok ? response.json() : null))
      .then(data => {
        if (cancelled) return;
        const remoteFlags = (data?.uiFlags && typeof data.uiFlags === 'object') ? data.uiFlags : {};
        setFlags(remoteFlags);
        try {
          localStorage.setItem(storageKey, JSON.stringify(remoteFlags));
        } catch {
          // Best-effort cache only.
        }
      })
      .catch(() => {
        // Preserve any local flags on read failure.
      })
      .finally(() => {
        if (!cancelled) {
          setInitialized(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apiBase, storageKey, userId]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test' || !enabled || !autoStart || !ready || !userId || !initialized || seen || autoStartedRef.current) {
      return;
    }
    autoStartedRef.current = true;
    setCurrent(0);
    setOpen(true);
  }, [autoStart, enabled, initialized, ready, seen, userId]);

  const start = useCallback(() => {
    setCurrent(0);
    setOpen(true);
  }, []);

  const close = useCallback(async () => {
    setOpen(false);
    setCurrent(0);
    setFlags(prev => {
      if (prev[flag]) return prev;
      const next = { ...prev, [flag]: true };
      if (storageKey) {
        try {
          localStorage.setItem(storageKey, JSON.stringify(next));
        } catch {
          // Best-effort cache only.
        }
      }
      return next;
    });
    if (!userId || process.env.NODE_ENV === 'test') {
      return;
    }
    try {
      await fetch(`${apiBase}/user/ui-flags/set`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await getAuthHeaders()) },
        body: JSON.stringify({ userId, flags: { [flag]: true } }),
      });
    } catch {
      // Optimistic local flag is sufficient for a non-critical tour close.
    }
  }, [apiBase, flag, storageKey, userId]);

  return {
    open,
    current,
    setCurrent,
    start,
    close,
    seen,
    initialized,
  };
}
